(() => {
  const ROOT_SELECTOR = '[data-ag-okendo-reviews-carousel]';
  const DEFAULT_TIMEOUT_MS = 9500;
  const AG_DEBUG = window.__AG_DEBUG__ === true;

  const debugLog = (...args) => {
    if (AG_DEBUG) console.log(...args);
  };

  const escapeHtml = (value) =>
    String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/\"/g, '&quot;')
      .replace(/'/g, '&#39;');

  const truncate = (text, maxChars) => {
    const input = String(text || '').trim();
    if (!input) return '';
    if (input.length <= maxChars) return input;
    return `${input.slice(0, Math.max(0, maxChars - 1)).trim()}…`;
  };

  const normalizeString = (value) => String(value ?? '').trim();

  const toNumber = (value, fallback = 0) => {
    const num = Number(value);
    return Number.isFinite(num) ? num : fallback;
  };

  const toPositiveIntegerString = (value) => {
    const raw = String(value ?? '').trim();
    if (!raw) return '';
    if (!/^\d+$/.test(raw)) return '';
    if (raw === '0') return '';
    return raw;
  };

  const getGlobalProductId = () => {
    const candidates = [
      window.meta && window.meta.product ? window.meta.product.id : null,
      window.ShopifyAnalytics && window.ShopifyAnalytics.meta && window.ShopifyAnalytics.meta.product
        ? window.ShopifyAnalytics.meta.product.id
        : null,
      window.__st && window.__st.rid ? window.__st.rid : null,
    ];

    for (const value of candidates) {
      const normalized = toPositiveIntegerString(value);
      if (normalized) return { productId: normalized, source: 'shopify_global' };
    }

    return { productId: '', source: 'missing' };
  };

  const resolveProductId = (root) => {
    const fromDataAttr = toPositiveIntegerString(root.dataset.productId);
    if (fromDataAttr) return { productId: fromDataAttr, source: 'section_data_attr' };
    return getGlobalProductId();
  };

  const getResponseArray = (payload) => {
    if (Array.isArray(payload)) return payload;
    if (!payload || typeof payload !== 'object') return [];
    if (Array.isArray(payload.reviews)) return payload.reviews;
    if (Array.isArray(payload.data)) return payload.data;
    if (payload.data && Array.isArray(payload.data.reviews)) return payload.data.reviews;
    return [];
  };

  const pickFirst = (obj, keys) => {
    if (!obj || typeof obj !== 'object') return '';
    for (const key of keys) {
      const value = obj[key];
      if (value != null && String(value).trim() !== '') return value;
    }
    return '';
  };

  const getPhotoUrls = (review) => {
    const candidates = [review?.photos, review?.images, review?.media];
    const urls = [];

    for (const candidate of candidates) {
      if (!Array.isArray(candidate)) continue;
      for (const item of candidate) {
        if (typeof item === 'string' && item.trim()) {
          urls.push(item.trim());
          continue;
        }
        if (item && typeof item === 'object') {
          const nested = [item.url, item.src, item.image, item.large, item.original, item.standard];
          const found = nested.find((value) => typeof value === 'string' && value.trim());
          if (typeof found === 'string') urls.push(found.trim());
        }
      }
    }

    return [...new Set(urls)];
  };

  const normalizeReview = (review, index) => {
    const reviewer = pickFirst(review, ['reviewer', 'reviewer_name', 'author', 'author_name', 'name']);
    const title = pickFirst(review, ['title', 'headline', 'review_title']);
    const body = pickFirst(review, ['body', 'content', 'review_body', 'text']);
    const date = pickFirst(review, ['date', 'created_at', 'published_at', 'submitted_at']);
    const id = pickFirst(review, ['id', 'review_id']) || `${reviewer}-${title}-${index}`;

    const ratingSource =
      review.rating ?? review.rating_value ?? review.score ?? review.stars ?? review.star_rating ?? review.review_rating;
    const rating = Math.max(0, Math.min(5, toNumber(ratingSource, 0)));

    const normalized = {
      id: String(id),
      title: normalizeString(title),
      body: normalizeString(body),
      reviewer: normalizeString(reviewer) || 'Verified buyer',
      rating,
      date: normalizeString(date),
      photos: getPhotoUrls(review),
    };

    const fingerprint = `${normalized.title} ${normalized.body} ${normalized.reviewer}`.trim();
    if (!fingerprint) return null;
    return normalized;
  };

  const renderStars = (rating) => {
    const rounded = Math.max(0, Math.min(5, Math.round(Number(rating) || 0)));
    const stars = Array.from({ length: 5 }, (_, index) => (index < rounded ? '★' : '☆')).join('');
    return `<p class="ag-reviews-carousel__rating" aria-label="${rounded} out of 5 stars">${stars}</p>`;
  };

  const renderStarsText = (rating) => {
    const rounded = Math.max(0, Math.min(5, Math.round(Number(rating) || 0)));
    return Array.from({ length: 5 }, (_, index) => (index < rounded ? '★' : '☆')).join('');
  };

  const formatDate = (value, locale) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);

    try {
      return new Intl.DateTimeFormat(locale || undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      }).format(date);
    } catch (_error) {
      return date.toISOString().slice(0, 10);
    }
  };

  const renderCard = (review, options) => {
    const photoUrl = Array.isArray(review.photos) && review.photos[0] ? escapeHtml(review.photos[0]) : '';
    if (!photoUrl) return '';

    const title = escapeHtml(review.title || '');
    const body = escapeHtml(review.body || '');
    const bodyPreview = escapeHtml(truncate(review.body || '', Math.min(options.maxChars || 180, 180)));
    const reviewer = escapeHtml(review.reviewer || 'Verified buyer');
    const date = options.showDate ? escapeHtml(formatDate(review.date, options.locale)) : '';
    const rating = String(Math.max(0, Math.min(5, Number(review.rating) || 0)));
    const ratingStarsText = escapeHtml(renderStarsText(review.rating));
    const photoAlt = escapeHtml(review.title || `Review photo from ${review.reviewer || 'customer'}`);
    const overlayMeta = [reviewer, date].filter(Boolean).join(' • ');
    const footerReviewer = reviewer ? `<p class="ag-reviews-carousel__photo-reviewer">${reviewer}</p>` : '';
    const footerStars = ratingStarsText ? `<p class="ag-reviews-carousel__photo-stars" aria-hidden="true">${ratingStarsText}</p>` : '';
    const footerMetaRow =
      footerReviewer || footerStars
        ? `
          <div class="ag-reviews-carousel__photo-meta-row">
            ${footerReviewer}
            ${footerStars}
          </div>
        `
        : '';
    const footerTitle = title ? `<p class="ag-reviews-carousel__photo-title">${title}</p>` : '';

    return `
      <article class="ag-reviews-carousel__card ag-reviews-carousel__card--photo-only">
        <div class="ag-reviews-carousel__media ag-reviews-carousel__media--square">
          <img
            class="ag-reviews-carousel__image"
            src="${photoUrl}"
            alt="${photoAlt}"
            loading="lazy"
            data-ag-review-lightbox-image="true"
            data-ag-review-rating="${escapeHtml(rating)}"
            data-ag-review-title="${title}"
            data-ag-review-body="${body}"
            data-ag-review-reviewer="${reviewer}"
            data-ag-review-date="${date}"
            data-ag-review-stars="${ratingStarsText}"
          >
          <div class="ag-reviews-carousel__hover-overlay" aria-hidden="true">
            <div class="ag-reviews-carousel__hover-overlay-inner">
              <p class="ag-reviews-carousel__hover-rating">${ratingStarsText}</p>
              ${title ? `<p class="ag-reviews-carousel__hover-title">${title}</p>` : ''}
              ${bodyPreview ? `<p class="ag-reviews-carousel__hover-text">${bodyPreview}</p>` : ''}
              ${overlayMeta ? `<p class="ag-reviews-carousel__hover-meta">${overlayMeta}</p>` : ''}
            </div>
          </div>
        </div>
        ${(footerMetaRow || footerTitle)
          ? `
          <div class="ag-reviews-carousel__photo-caption">
            ${footerMetaRow}
            ${footerTitle}
          </div>
        `
          : ''}
      </article>
    `;
  };

  const setStatus = (root, message, type = '') => {
    const status = root.querySelector('[data-ag-okendo-status]');
    if (!status) return;
    status.textContent = message;
    status.hidden = !message;
    status.dataset.state = type;
  };

  const fetchJsonWithTimeout = async (url, timeoutMs) => {
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timeoutId = controller
      ? window.setTimeout(() => {
          controller.abort();
        }, timeoutMs)
      : null;

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
        },
        credentials: 'same-origin',
        signal: controller ? controller.signal : undefined,
      });

      const contentType = response.headers.get('content-type') || '';
      const rawBody = await response.text();

      if (!contentType.includes('application/json')) {
        return { response, body: rawBody };
      }

      if (!rawBody.trim()) {
        return { response, body: '' };
      }

      let body;
      try {
        body = JSON.parse(rawBody);
      } catch (error) {
        const parseError = new Error(`Invalid JSON response from reviews proxy (${response.status})`);
        parseError.name = 'InvalidJsonResponseError';
        parseError.httpStatus = response.status;
        parseError.contentType = contentType;
        parseError.bodyPreview = rawBody.slice(0, 300);
        throw parseError;
      }

      return { response, body };
    } finally {
      if (timeoutId) window.clearTimeout(timeoutId);
    }
  };

  const buildProxyUrl = (root, options) => {
    const proxyPath = (root.dataset.proxyPath || '').trim();
    if (!proxyPath) return null;

    const url = new URL(proxyPath, window.location.origin);
    url.searchParams.set('product_id', String(options.productId || ''));
    url.searchParams.set('locale', options.locale || '');
    url.searchParams.set('limit', String(options.reviewLimit));
    url.searchParams.set('min_rating', String(options.minRating));
    return url;
  };

  const sortReviewsByDateDesc = (reviews) =>
    [...reviews].sort((a, b) => {
      const aTime = new Date(a.date || 0).getTime();
      const bTime = new Date(b.date || 0).getTime();
      return (Number.isNaN(bTime) ? 0 : bTime) - (Number.isNaN(aTime) ? 0 : aTime);
    });

  const initCarousel = async (root) => {
    if (root.dataset.agOkendoInit === 'true') return;
    root.dataset.agOkendoInit = 'true';

    const reviewLimit = Math.max(1, Math.min(20, Number(root.dataset.reviewLimit || 6)));
    const proxyFetchLimit = Math.max(reviewLimit, Math.min(20, reviewLimit * 3));
    const maxChars = Math.max(60, Math.min(600, Number(root.dataset.bodyMaxChars || 180)));
    const locale = (root.dataset.locale || '').trim();
    const showDate = root.dataset.showDate === 'true';
    const minRating = Math.max(1, Math.min(5, Number(root.dataset.minRating || 1)));
    const productIdInfo = resolveProductId(root);
    const productId = productIdInfo.productId;
    const timeoutMs = Math.max(500, Math.min(9500, Number(root.dataset.fetchTimeoutMs || DEFAULT_TIMEOUT_MS)));
    const productHandle = (root.dataset.productHandle || '').trim();

    const track = root.querySelector('[data-ag-okendo-track]');
    if (!track) return;

    const proxyUrl = buildProxyUrl(root, { productId, locale, reviewLimit: proxyFetchLimit, minRating });
    if (!proxyUrl) {
      setStatus(root, 'Proxy path is not configured. Set an app proxy path in the section settings.', 'error');
      window.__agOkendoReviewsCarouselDebug = {
        mode: 'proxy',
        error: 'missing_proxy_path',
      };
      return;
    }

    if (!productId) {
      setStatus(root, 'Product ID is unavailable on this page. Use this section on a product template.', 'error');
      window.__agOkendoReviewsCarouselDebug = {
        mode: 'proxy',
        error: 'missing_product_id',
        productIdSource: productIdInfo.source,
        productHandle,
      };
      console.warn('[AG Okendo Reviews Carousel] missing product id', window.__agOkendoReviewsCarouselDebug);
      return;
    }

    debugLog('[AG Okendo Reviews Carousel] starting proxy fetch', {
      productId,
      productIdSource: productIdInfo.source,
      productHandle,
      proxyUrl: proxyUrl.toString(),
      timeoutMs,
      options: { locale, reviewLimit, proxyFetchLimit, minRating, maxChars, showDate },
    });

    setStatus(root, 'Loading reviews…', 'loading');

    try {
      const { response, body } = await fetchJsonWithTimeout(proxyUrl, timeoutMs);

      debugLog('[AG Okendo Reviews Carousel] proxy response received', {
        proxyUrl: proxyUrl.toString(),
        httpStatus: response.status,
        ok: response.ok,
        body,
      });

      if (!response.ok) {
        const message = `Reviews proxy error (${response.status}). Check app proxy configuration and endpoint response.`;
        setStatus(root, message, 'error');
        window.__agOkendoReviewsCarouselDebug = {
          mode: 'proxy',
          proxyUrl: proxyUrl.toString(),
          timeoutMs,
          httpStatus: response.status,
          responseBody: body,
        };
        console.warn('[AG Okendo Reviews Carousel] proxy error', window.__agOkendoReviewsCarouselDebug);
        return;
      }

      const normalizedReviews = getResponseArray(body)
        .map((review, index) => normalizeReview(review, index))
        .filter(Boolean)
        .filter((review) => Number(review.rating || 0) >= minRating);

      const photoReviews = sortReviewsByDateDesc(normalizedReviews).filter(
        (review) => Array.isArray(review.photos) && review.photos.length > 0,
      );
      const renderedReviews = photoReviews.slice(0, reviewLimit);

      if (!renderedReviews.length) {
        track.innerHTML = '';
        track.classList.remove('ag-reviews-carousel__track--single');
        setStatus(root, 'No review photos available for this product yet.', 'empty');
        window.__agOkendoReviewsCarouselDebug = {
          mode: 'proxy',
          proxyUrl: proxyUrl.toString(),
          timeoutMs,
          rawResponse: body,
          extractedReviews: normalizedReviews,
          photoReviews,
          renderedReviews,
          options: {
            productId,
            productIdSource: productIdInfo.source,
            locale,
            reviewLimit,
            proxyFetchLimit,
            minRating,
            maxChars,
            showDate,
          },
        };
        console.warn('[AG Okendo Reviews Carousel] no reviews returned', window.__agOkendoReviewsCarouselDebug);
        return;
      }

      track.innerHTML = renderedReviews
        .map((review) => renderCard(review, { locale, maxChars, showDate }))
        .join('');
      track.classList.toggle('ag-reviews-carousel__track--single', renderedReviews.length === 1);
      setStatus(root, '', '');

      window.__agOkendoReviewsCarouselDebug = {
        mode: 'proxy',
        proxyUrl: proxyUrl.toString(),
        timeoutMs,
        rawResponse: body,
        extractedReviews: normalizedReviews,
        photoReviews,
        renderedReviews,
        options: {
          productId,
          productIdSource: productIdInfo.source,
          locale,
          reviewLimit,
          proxyFetchLimit,
          minRating,
          maxChars,
          showDate,
        },
      };
      debugLog('[AG Okendo Reviews Carousel] rendered reviews from proxy', window.__agOkendoReviewsCarouselDebug);
    } catch (error) {
      const timedOut = error && error.name === 'AbortError';
      const message = timedOut
        ? `Reviews proxy timed out after ${timeoutMs}ms.`
        : 'Failed to fetch reviews from proxy. Check app proxy route and response format.';

      setStatus(root, message, 'error');
      window.__agOkendoReviewsCarouselDebug = {
        mode: 'proxy',
        proxyUrl: proxyUrl.toString(),
        timeoutMs,
        errorName: error?.name || null,
        errorMessage: error?.message || String(error),
        httpStatus: error?.httpStatus || null,
        contentType: error?.contentType || null,
        bodyPreview: error?.bodyPreview || null,
      };
      console.warn('[AG Okendo Reviews Carousel] proxy fetch failed', window.__agOkendoReviewsCarouselDebug);
    }
  };

  const initAll = () => {
    document.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      initCarousel(root);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
})();
